import { select } from "d3-selection";
import { timeParse } from "d3-time-format";
import { scaleLinear, scaleTime } from "d3-scale";
import { extent, max } from "d3-array";
import { line as drawLine, area as drawArea, curveBasis } from "d3-shape";
import { transition } from "d3-transition";
import { easeSinInOut } from "d3-ease";
import localChartData from "./micro-cap.json";
import { isMobile } from "components/utility/page-responsive/PageResponsive";

const drawMicroCapClipPath = (microCapContainerDiv) => {
	if (microCapContainerDiv.length) {
		//settings
		let animationDuration = 1900;
		// draw chart
		microCapContainerDiv.forEach((microCapDiv) => {
			let chartWindowObj = [];
			if (process.env.NODE_ENV === "development") {
				chartWindowObj = localChartData.data;
			} else {
				const guid = microCapDiv.getAttribute("data-guid");
				chartWindowObj = window[`microcap_${guid}`].data;
			}

			const chartData = chartWindowObj;

			// parse the date / time
			const parseTime = timeParse("%m/%d/%Y");
			chartData.forEach((d) => {
				d.date = parseTime(d.year);
				d.fund = +d.fund;
				d.index = +d.index;
			});

			const width = 1000;
			const height = 400;

			const setupChart = (chartData, value, width, height) => {
				// set the x/y range/scale
				const { fund: finalFundValue, index: finalIndexValue } =
					chartWindowObj[chartWindowObj.length - 1];
				height = value === "fund" ? 400 : (400 * (finalIndexValue / finalFundValue)).toFixed(3);
				const xScale = scaleTime().range([0 + 2, width - 10]);
				const yScale = scaleLinear().range([height, 0]);
				xScale.domain(
					extent(chartData, (d) => {
						return d.date;
					})
				);
				yScale.domain([
					0,
					max(chartData, (d) => {
						return d[value];
					})
				]);

				// append the svg obgect to the div parent
				const svg = select(microCapDiv)
					.append("svg")
					.attr("class", `micro-cap-svg-${value}`)
					.attr("preserveAspectRatio", "xMinYMin meet")
					.attr("viewBox", `0 0  ${width} ${height}`);

				// appends a 'group' element to 'svg'
				const group = svg.append("g");

				// add clip path
				const clip = svg.append("clipPath").attr("id", `clip-${value}`);
				clip
					.append("rect")
					.attr("id", `micro-cap-rectClip-${value}`)
					.attr("width", isMobile() ? "100%" : 0)
					.attr("height", height);

				// set area fill gradient
				let gradientProps = {
					id: `ddiff-microcap-chart-gradient-${value}`,
					x1: "0%",
					x2: "100%",
					y1: "0%",
					y2: "0%",
					transform: "rotate(156)",
					offset1: "0%",
					stopColor1: "#62E0C9",
					stopOpacity1: 0.75,
					offset2: "0%",
					stopColor2: "#00919C",
					stopOpacity2: 0.75
				};
				switch (value) {
					case "fund":
						gradientProps = {
							id: `ddiff-microcap-chart-gradient-${value}`,
							x1: "0%",
							x2: "90%",
							y1: "0%",
							y2: "110%",
							offset1: "58.88%",
							stopColor1: "rgba(98, 224, 201, 0.90)",
							offset2: "86.64%",
							stopColor2: "rgba(0, 145, 156, 0.90)"
						};
						break;
					case "index":
						gradientProps = {
							id: `ddiff-microcap-chart-gradient-${value}`,
							x1: "0%",
							x2: "90%",
							y1: "0%",
							y2: "110%",
							offset1: "12.65%",
							stopColor1: "#A2AAAD",
							offset2: "30.92%",
							stopColor2: "#C7CCCE"
						};
						break;
					default:
						gradientProps = { ...gradientProps };
				}
				const defs = svg.append("defs");
				const gradient = defs
					.append("linearGradient")
					.attr("id", gradientProps.id)
					.attr("x1", gradientProps.x1)
					.attr("x2", gradientProps.x2)
					.attr("y1", gradientProps.y1)
					.attr("y2", gradientProps.y2)
					.attr("gradientTransform", gradientProps.transform);

				gradient
					.append("stop")
					.attr("class", "start")
					.attr("offset", gradientProps.offset1)
					.attr("stop-color", gradientProps.stopColor1)
					.attr("stop-opacity", gradientProps.stopOpacity1);

				gradient
					.append("stop")
					.attr("class", "end")
					.attr("offset", gradientProps.offset2)
					.attr("stop-color", gradientProps.stopColor2)
					.attr("stop-opacity", gradientProps.stopOpacity2);

				// add area
				group
					.append("path")
					.datum(chartData)
					.attr("fill", `url(#ddiff-microcap-chart-gradient-${value})`)
					.attr(
						"d",
						drawArea()
							.x((d) => {
								return xScale(d.date);
							})
							.y0(yScale(0))
							.y1((d) => {
								return yScale(d[value]);
							})
							.curve(curveBasis)
					)
					.attr("opacity", 0.75);

				// add the line
				const line = group
					.append("path")
					.attr("class", "line")
					.datum(chartData)
					.attr(
						"d",
						drawLine()
							.x((d) => {
								return xScale(d.date);
							})
							.y((d) => {
								return yScale(d[value]);
							})
							.curve(curveBasis)
					)
					.attr("fill", "none")
					.attr("stroke", () => {
						return value === "fund" ? "#00919c" : "#6e787f";
					})
					.attr("stroke-width", 2)
					.attr("opacity", 0.9);

				group.attr("clip-path", `url(#clip-${value})`);
				const length = line.node().getTotalLength();

				group
					.append("circle")
					.attr("class", "starting-point")
					.attr("fill", "none")
					.attr("opacity", 0)
					.attr("r", 0.5)
					.attr("transform", () => {
						const p = line.node().getPointAtLength(0);
						return "translate(" + [p.x, p.y] + ")";
					});

				group
					.append("circle")
					.attr("class", "end-point")
					.attr("fill", "none")
					.attr("opacity", 0)
					.attr("r", 0.5)
					.attr("transform", () => {
						const p = line.node().getPointAtLength(length);
						return "translate(" + [p.x, p.y] + ")";
					});
			};
			setupChart(chartData, "fund", width, height);
			setupChart(chartData, "index", width, height);

			// monitor the start label element
			// once its animate class is added check for the end of the animation
			// when the animation ends trigger the transition of the line/area
			const mutationCallback = (mutations) => {
				for (let mutation of mutations) {
					if (mutation.target.classList.contains("ddiff-micro-cap-chart-exhibit-floor-animate")) {
						mutation.target.addEventListener("animationend", () => {
							const t = transition().duration(animationDuration).ease(easeSinInOut);
							// run graph animations
							["fund", "index"].forEach((value) => {
								const clipRectToWiden = select(`.micro-cap-svg-${value}`).select(
									`#micro-cap-rectClip-${value}`
								);
								clipRectToWiden.transition(t).attr("width", "100%");
							});

							observer.disconnect();
						});
					}
				}
			};
			// the chart moving line/area should not animate until start label ends its animation
			// grab the start label and monitor using a mutationObserver
			const target = document.querySelector(".ddiff-micro-cap-wrapper");

			const observer = new MutationObserver(mutationCallback);

			observer.observe(target, {
				attributes: true,
				attributeFilter: ["style", "class"],
				subtree: true
			});
		});
	}
};

export { drawMicroCapClipPath };
